import { normalize } from 'normalizr'

import Raven from 'lib/raven'
import * as userUtil from 'utils/user-utils'
import * as appActions from './app'
import * as scoreActions from './scores'
import * as schema from 'lib/schema'
import api from 'lib/api'

const QUEUE_CRUSHES = 'crush/crushes/QUEUE_CRUSHES'
const BACKGROUND_FETCH_CRUSHES = 'crush/crushes/BACKGROUND_FETCH_CRUSHES'
const SET_CRUSH_PENDING = 'crush/crushes/SET_CRUSH_PENDING'
const SET_CRUSH_LIKED = 'crush/crushes/SET_CRUSH_LIKED'
const SET_CRUSH_DISLIKED = 'crush/crushes/SET_CRUSH_DISLIKED'
const SET_CRUSH_REPORTED = 'crush/crushes/SET_CRUSH_REPORTED'
const REVOKE_CRUSH = 'crush/crushes/REVOKE_CRUSH'

export const CRUSH_ACTIONS = {
  LIKE: 'like',
  DISLIKE: 'dislike',
  REVOKE: 'revoke'
}

const initialState = {
  isFetchingNewCrushesInBackground: false,
  lastAction: null,
  entities: {},
  queuedCrushes: [],
  pendingCrushes: [],
  likedCrushes: [],
  dislikedCrushes: [],
  revokedCrushes: [],
  reportedCrushes: []
}

function cleanCrushEntities(crushes) {
  return Object.keys(crushes).reduce((acc, crushId) => {
    const crush = crushes[crushId]

    acc[crushId] = {
      ...crush,
      pictures: userUtil.cleanPictures(crush.pictures, crush.profilePic)
    }
    return acc
  }, {})
}

export default function (state = initialState, action) {
  switch (action.type) {
    case appActions.ADD_ENTITIES:
      // Clean pictures in crushes
      const crushes = action.entities.crushes
        ? cleanCrushEntities(action.entities.crushes)
        : {}

      return {
        ...state,
        entities: {
          ...state.entities,
          ...crushes
        }
      }

    case QUEUE_CRUSHES:
      const onlyNewCrushes = action.crushIds.filter(
        crushId =>
          !state.queuedCrushes.includes(crushId) &&
          !state.pendingCrushes.includes(crushId) &&
          !state.likedCrushes.includes(crushId) &&
          !state.dislikedCrushes.includes(crushId) &&
          !state.reportedCrushes.includes(crushId)
      )

      return {
        ...state,
        queuedCrushes: [...state.queuedCrushes, ...onlyNewCrushes]
      }

    case BACKGROUND_FETCH_CRUSHES:
      return {
        ...state,
        isFetchingNewCrushesInBackground: action.isFetching
      }

    case SET_CRUSH_REPORTED:
      return {
        ...state,
        queuedCrushes: state.queuedCrushes.filter(
          queuedCrush => queuedCrush !== action.crushId
        ),
        reportedCrushes: [action.crushId, ...state.reportedCrushes]
      }

    case SET_CRUSH_PENDING:
      return {
        ...state,
        queuedCrushes: state.queuedCrushes.filter(
          queuedCrush => queuedCrush !== action.crushId
        ),
        revokedCrushes: state.revokedCrushes.filter(
          revokedCrush => revokedCrush !== action.crushId
        ),
        pendingCrushes: [...state.pendingCrushes, action.crushId]
      }

    case SET_CRUSH_LIKED:
      return {
        ...state,
        lastAction: CRUSH_ACTIONS.LIKE,
        pendingCrushes: state.pendingCrushes.filter(
          pendingCrush => pendingCrush !== action.crushId
        ),
        likedCrushes: [action.crushId, ...state.likedCrushes]
      }

    case SET_CRUSH_DISLIKED:
      return {
        ...state,
        lastAction: CRUSH_ACTIONS.DISLIKE,
        pendingCrushes: state.pendingCrushes.filter(
          pendingCrush => pendingCrush !== action.crushId
        ),
        dislikedCrushes: [action.crushId, ...state.dislikedCrushes]
      }

    case REVOKE_CRUSH:
      return {
        ...state,
        lastAction: CRUSH_ACTIONS.REVOKE,
        queuedCrushes: [action.crushId, ...state.queuedCrushes],
        dislikedCrushes: state.dislikedCrushes.filter(
          dislikedCrush => dislikedCrush !== action.crushId
        ),
        likedCrushes: state.likedCrushes.filter(
          likedCrush => likedCrush !== action.crushId
        ),
        revokedCrushes: [action.crushId, ...state.revokedCrushes]
      }

    default:
      return state
  }
}

export const setBackgroundFetch = isFetching => ({
  type: BACKGROUND_FETCH_CRUSHES,
  isFetching
})

export const setCrushReported = crushId => ({
  type: SET_CRUSH_REPORTED,
  crushId
})

export const setCrushPending = crushId => ({
  type: SET_CRUSH_PENDING,
  crushId
})

export const setCrushLiked = crushId => ({
  type: SET_CRUSH_LIKED,
  crushId
})

export const setCrushDisliked = crushId => ({
  type: SET_CRUSH_DISLIKED,
  crushId
})

export const revokeCrush = crushId => ({
  type: REVOKE_CRUSH,
  crushId
})

export const queueCrushes = crushIds => ({
  type: QUEUE_CRUSHES,
  crushIds
})

export const fetchCrush = id => (dispatch, getState) => {
  const { credentials } = getState().user
  const savedProfiles = getState().crushes.entities

  // Don't fetch profile if it was already loaded
  if (savedProfiles[id]) {
    return Promise.resolve(savedProfiles[id])
  }

  return api.profile
    .get(id, credentials)
    .then(({ targetUser }) => {
      const { entities } = normalize(targetUser, schema.crush)
      dispatch(appActions.addEntities(entities))

      return targetUser
    })
    .catch(error => {
      dispatch(appActions.showFetchError())
      Raven.captureException(error)
    })
}

export const fetchCrushes = (inBackground = false) => (dispatch, getState) => {
  const { credentials } = getState().user
  return api.likedislike
    .get(credentials)
    .then(({ users }) => {
      if (!users) {
        throw new Error(
          'Invalid response when fetching crushes, no users in respose.'
        )
      }
      if (users[0].firstName === 'fb-returned-empty-psid') {
        dispatch(appActions.showErrorAd())
        return
      }

      const { result, entities } = normalize(users, [schema.crush])
      dispatch(appActions.addEntities(entities))
      dispatch(queueCrushes(result))

      return users
    })
    .catch(error => {
      dispatch(appActions.showFetchError())

      // Don't report background fetch errors to Sentry. We don't want to
      // spam it.
      !inBackground && Raven.captureException(error)
    })
}

export const fetchCrushesInBackground = () => (dispatch, getState) => {
  const { crushes } = getState()
  dispatch(setBackgroundFetch(true))

  // Abort if enough queued crushes are available or if it's currently fetching
  if (crushes.queuedCrushes.length > 9 || crushes.isBackgroundFetching) {
    return Promise.resolve()
  }

  return dispatch(fetchCrushes(true)).then(() =>
    dispatch(setBackgroundFetch(false))
  )
}

export const likeCrush = crushId => (dispatch, getState) => {
  const { credentials } = getState().user
  const crushes = getState().crushes.entities

  dispatch(setCrushPending(crushId))
  return api.likedislike
    .setLike(crushes[crushId], credentials)
    .then(scores => {
      dispatch(setCrushLiked(crushId))
      return dispatch(
        scoreActions.receiveNewScores(scores, { type: CRUSH_ACTIONS.LIKE })
      )
    })
    .catch(error => {
      Raven.captureException(error)
    })
}

export const dislikeCrush = crushId => (dispatch, getState) => {
  const { credentials } = getState().user
  const crushes = getState().crushes.entities

  dispatch(setCrushPending(crushId))
  return api.likedislike
    .setDislike(crushes[crushId], credentials)
    .then(scores => {
      dispatch(setCrushDisliked(crushId))
      return dispatch(
        scoreActions.receiveNewScores(scores, { type: CRUSH_ACTIONS.DISLIKE })
      )
    })
    .catch(error => {
      Raven.captureException(error)
    })
}

export const revokeLastDislikedCrush = () => (dispatch, getState) => {
  const { credentials } = getState().user
  const { dislikedCrushes, entities: crushes } = getState().crushes
  const [lastDislikedCrushId] = dislikedCrushes

  dispatch(revokeCrush(lastDislikedCrushId))
  return api.likedislike
    .revokeAction(crushes[lastDislikedCrushId], credentials)
    .then(scores => {
      return dispatch(
        scoreActions.receiveNewScores(scores, { type: CRUSH_ACTIONS.REVOKE })
      )
    })
    .catch(error => {
      Raven.captureException(error)
    })
}

export const reportCrush = (crushId, report) => (dispatch, getState) => {
  const { credentials } = getState().user

  dispatch(setCrushReported(crushId))
  return api.profile.report(crushId, report, credentials).catch(error => {
    Raven.captureException(error)
  })
}



// WEBPACK FOOTER //
// ./src/lib/store/crushes.js